import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import LoadingSpinner from '../components/common/LoadingSpinner';
import immigrationConsultancyService from '../services/immigrationConsultancyService';

const PolicyDetailsPage = () => {
  const { slug } = useParams();
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadPolicyDetails = useCallback(async () => {
    try {
      setLoading(true);
      const policyData = await immigrationConsultancyService.getPolicy(slug);
      setPolicy(policyData);
    } catch (error) {
      console.error('Error loading policy details:', error);
      setError('Failed to load policy details');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    loadPolicyDetails();
  }, [loadPolicyDetails]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !policy) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Policy Not Found</h1>
          <p className="text-gray-600 mb-8">The policy you're looking for doesn't exist or has been removed.</p>
          <Link 
            to="/visa" 
            className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Visa Information
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-green-600/90 via-emerald-700/90 to-teal-800/90"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-6">
              <span className="text-white/90 text-sm font-medium tracking-wide uppercase">
                {policy.country_name} • {policy.priority.toUpperCase()} Priority
              </span>
            </div>
            <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              {policy.title}
            </h1>
            <p className="text-xl text-green-50 max-w-3xl mx-auto mb-8 leading-relaxed">
              {policy.summary}
            </p>
            <Link
              to="/visa"
              className="inline-flex items-center px-6 py-3 bg-white text-green-600 rounded-full font-semibold hover:bg-green-50 transition-all duration-300"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Visa Information
            </Link>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Policy Image */}
            <div className="relative h-64 rounded-3xl overflow-hidden mb-8 shadow-2xl">
              <img
                src={immigrationConsultancyService.getPolicyImage(policy.priority, policy.country_code)}
                alt={`${policy.title} - ${policy.country_name}`}
                className="w-full h-full object-cover"
                onError={(e) => immigrationConsultancyService.handleImageError(e, policy.country_code)}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
              <div className="absolute bottom-6 left-6 right-6">
                <h2 className="text-2xl font-bold text-white mb-2">Policy Update</h2>
                <p className="text-green-100 leading-relaxed">{policy.summary}</p>
              </div>
            </div>

            {/* Full Content (fallback to summary if content is empty) */}
            {(policy.content || policy.summary) && (
              <div className="bg-white rounded-3xl shadow-xl p-8 mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Full Policy Details</h3>
                <div className="prose prose-lg max-w-none">
                  <div className="text-gray-700 leading-relaxed whitespace-pre-line">
                    {policy.content || policy.summary}
                  </div>
                </div>
              </div>
            )}

            {/* Impact Section */}
            {policy.impact && (
              <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-3xl shadow-xl p-8 mb-8 border border-yellow-200">
                <div className="flex items-start">
                  <svg className="w-8 h-8 text-yellow-600 mt-1 mr-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <h3 className="text-2xl font-bold text-yellow-800 mb-4">Impact on Applicants</h3>
                    <p className="text-yellow-700 leading-relaxed text-lg">{policy.impact}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            {/* Key Information Card */}
            <div className="bg-white rounded-3xl shadow-xl p-8 mb-8 sticky top-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Policy Information</h3>
              
              <div className="space-y-6">
                <div className="flex items-center justify-between p-4 bg-blue-50 rounded-2xl border border-blue-100">
                  <div className="flex items-center">
                    <span className="text-2xl mr-3">🏛️</span>
                    <span className="font-semibold text-gray-700">Country</span>
                  </div>
                  <span className="font-bold text-blue-600 text-lg">{policy.country_flag} {policy.country_name}</span>
                </div>
                
                <div className="flex items-center justify-between p-4 bg-purple-50 rounded-2xl border border-purple-100">
                  <div className="flex items-center">
                    <span className="text-2xl mr-3">⚡</span>
                    <span className="font-semibold text-gray-700">Priority</span>
                  </div>
                  <span className={`font-bold text-lg px-3 py-1 rounded-full ${immigrationConsultancyService.getPriorityColor(policy.priority)}`}>
                    {policy.priority.toUpperCase()}
                  </span>
                </div>
                
                {policy.visa_type_name && (
                  <div className="flex items-center justify-between p-4 bg-green-50 rounded-2xl border border-green-100">
                    <div className="flex items-center">
                      <span className="text-2xl mr-3">📋</span>
                      <span className="font-semibold text-gray-700">Visa Type</span>
                    </div>
                    <span className="font-bold text-green-600 text-lg">{policy.visa_type_name}</span>
                  </div>
                )}
                
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl border border-gray-100">
                  <div className="flex items-center">
                    <span className="text-2xl mr-3">📅</span>
                    <span className="font-semibold text-gray-700">Published</span>
                  </div>
                  <span className="font-bold text-gray-600 text-lg">{policy.time_ago}</span>
                </div>
                
                {policy.source_name && (
                  <div className="flex items-center justify-between p-4 bg-indigo-50 rounded-2xl border border-indigo-100">
                    <div className="flex items-center">
                      <span className="text-2xl mr-3">🔗</span>
                      <span className="font-semibold text-gray-700">Source</span>
                    </div>
                    <span className="font-bold text-indigo-600 text-lg">{policy.source_name}</span>
                  </div>
                )}
              </div>

              {/* Source Information */}
              <div className="mt-6 p-4 bg-gray-50 rounded-2xl border border-gray-100">
                <h4 className="text-sm font-bold text-gray-700 mb-2">Policy Source</h4>
                <p className="text-sm text-gray-600 mb-3">
                  Source: <span className="font-semibold">{policy.source_name}</span>
                </p>
                {policy.source_url && (
                  <a
                    href={policy.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                    View Official Source
                  </a>
                )}
                <div className="mt-3 flex items-center text-xs text-gray-500">
                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Published: {new Date(policy.effective_date).toLocaleDateString()}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="mt-8 pt-6 border-t border-gray-100">
                <button className="w-full inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-2xl hover:from-green-700 hover:to-emerald-700 transition-all duration-300 font-bold shadow-lg hover:shadow-xl transform hover:scale-105">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  Get Expert Guidance
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PolicyDetailsPage;
